/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, useCallback, useState } from 'react';
import { Typography, Stack, Alert, useMediaQuery, useTheme } from '@mui/material';
import TestIds from '../../../../testIds';
import Modal from '../../../../components/Modal';
import { Campaign, Lookup, ActivityStatus, Activity } from '../../api/types/types';
import { getLookupByName } from '../../utils/lookupUtils';
import { useUpdateActivity } from '../../api/hooks/useActivities';
import { CampaignFormData } from './types';
import { transformFormDataToCampaignResponses } from './transformUtils';
import useToast from '../../../../util/useToast';
import { log } from '../../../../util/log';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { useCampaignForm } from './useCampaignForm';
import CampaignFormContent from './CampaignFormContent';
import ActivityModalActionBar from '../ActivityModalActionBar/ActivityModalActionBar';

export interface CompleteActivityModalProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => Promise<void>;
  accountNumber: string;
  activity?: Activity;
  campaigns?: Campaign[];
  activityName?: string;
  lookups?: Lookup[];
}

const CompleteActivityModal: FC<CompleteActivityModalProps> = ({
  open,
  onClose,
  onComplete,
  activity,
  activityName,
  accountNumber,
  campaigns = [],
  lookups = [],
}): JSX.Element => {
  const { notify } = useToast();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [isRefetching, setIsRefetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutateAsync: updateActivity } = useUpdateActivity(accountNumber);
  const responseCodeLookup = getLookupByName(lookups, 'CampaignResponseCodes');
  const reasonLookup = getLookupByName(lookups, 'CampaignResponseReasons');
  const typeLookup = getLookupByName(lookups, 'CampaignResponseTypes');
  const competitorLookup = getLookupByName(lookups, 'Competitors');

  const campaignForm = useCampaignForm({
    campaigns,
    reasonLookup,
    typeLookup,
    competitorLookup,
    resetOnOpen: true,
    open,
  });

  const {
    methods,
    validateForm,
  } = campaignForm;

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    trigger,
  } = methods;

  const onSubmit = useCallback(
    async (formData: Record<string, CampaignFormData>) => {
      setErrorMessage(null);
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      try {
        const campaignResponses = transformFormDataToCampaignResponses(
          campaigns,
          formData,
          competitorLookup,
        );
        if (!activity?.activityId) {
          throw new Error('Activity ID is required');
        }

        await updateActivity({
          activityId: activity.activityId,
          activityType: activity.activityType,
          accountNumber,
          status: ActivityStatus.Completed,
          dueDate: activity.dueDate,
          scheduleDate: activity.scheduleDate,
          contactId: activity.contactId,
          subject: activity.subject,
          description: activity.description,
          to: activity.to,
          toType: activity.toType,
          duration: activity.duration,
          ...(campaignResponses.length && { campaignResponses }),
        });

        reset();

        if (onComplete) {
          setIsRefetching(true);
          try {
            await onComplete();
          } finally {
            setIsRefetching(false);
          }
        }

        notify('Activity completed', {
          type: 'success',
          icon: <MaterialSymbol>check_circle</MaterialSymbol>,
        });
        onClose();
      } catch (error) {
        setIsRefetching(false);
        setErrorMessage(
          'Sorry, something went wrong. Please try again, or contact us if the problem persists.',
        );
        log('Failed to complete activity:', error);
      }
    },
    [
      activity,
      accountNumber,
      campaigns,
      competitorLookup,
      onComplete,
      onClose,
      validateForm,
      reset,
      trigger,
      notify,
    ],
  );

  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDialogClose = useCallback(
    (event: object, reason?: string) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return;
      }
      onClose();
    },
    [onClose],
  );

  return (
    <Modal
      title='Complete Activity'
      open={open}
      handleClose={handleModalClose}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth={campaigns.length === 0 ? 'sm' : 'md'}
      data-testid={TestIds.feat.activities.completeActivityModal.component}
      header={
        campaigns.length === 0 ? null : (
          <Typography variant='body2' color='text.secondary'>
            Track the campaigns you discussed and add response details
          </Typography>
        )
      }
      disableMobileFullScreen={campaigns.length === 0}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
          <CampaignFormContent
            campaigns={campaigns}
            responseCodeLookup={responseCodeLookup}
            competitorLookup={competitorLookup}
            activityName={activityName}
            campaignForm={campaignForm}
          />
        </Stack>
        <ActivityModalActionBar
          showLoadingIndicator={isSubmitting || isRefetching}
          loadingIndicatorText="Completing activity"
          loadingIndicatorTestId={TestIds.feat.activities.completeActivityModal.loading}
          closeButtonText='Cancel'
          closeButtonTestId={TestIds.feat.activities.completeActivityModal.cancelButton}
          submitButtonText={campaigns.length === 0 && isSm ? 'Complete' : 'Complete Activity'}
          submitButtonTestId={TestIds.feat.activities.completeActivityModal.completeButton}
          onClose={onClose}
          onSubmit={handleSubmit(onSubmit)}
        />
      </form>
    </Modal>
  );
};

export default CompleteActivityModal;
